/**
 * Kunduzgi / tungi rejim. Tanlov uch xil: "light", "dark" yoki "system" (tizimga ergashadi). Tanlov shu qurilmada
 * (localStorage) saqlanadi, boshqa tablar ergashadi; "system" -- saqlanmaydi (kalit o'chiriladi). Boshlang'ich klass
 * index.html'dagi kichik skript bilan React'dan oldin qo'yiladi (oq chaqnash yo'q).
 * Almashish animatsiyasi: index.css (::view-transition-new) -- yangi rejim tugma tomondan aniq qiya parda bo'lib kiradi.
 */
import { useSyncExternalStore } from "react";
import { flushSync } from "react-dom";

export type Theme = "light" | "dark";
export type ThemePreference = Theme | "system";

export const THEME_KEY = "dh.theme";

/** Saqlangan tanlov, bo'lmasa tizim sozlamasi */
export function resolveTheme(stored: string | null, systemDark: boolean): Theme {
  if (stored === "dark" || stored === "light") return stored;
  return systemDark ? "dark" : "light";
}

function readStored(): string | null {
  try {
    return localStorage.getItem(THEME_KEY);
  } catch {
    return null;
  }
}

function systemDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
}

let current: Theme = typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light";
const listeners = new Set<() => void>();

function apply(next: Theme) {
  current = next;
  document.documentElement.classList.toggle("dark", next === "dark");
  listeners.forEach((l) => l());
}

export function getTheme(): Theme {
  return current;
}

/** Foydalanuvchi tanlovi: saqlangan rejim yoki "system" */
export function getPreference(): ThemePreference {
  const stored = readStored();
  return stored === "dark" || stored === "light" ? stored : "system";
}

export function subscribeTheme(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, getTheme, getTheme);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribeTheme, getPreference, getPreference);
}

/** Parda qiyaligi (index.css'dagi gradient burchagi bilan bir xil) va yumshoq qismi (ekran enining ulushi) */
export const CURTAIN_ANGLE = 100;
const CURTAIN_FADE = 0.14;

/**
 * Parda niqobi geometriyasi: chegara animatsiya boshida aynan o'ng chetdan tashqarida, oxirida aynan chap chetdan
 * tashqarida bo'lsin -- har qanday ekran nisbatida (tik telefonda qiyalik kengroq joy oladi). Natija -- index.css'dagi
 * --vt-size (qatlam eni, %), --vt-from/--vt-to (gradient yumshoq qismi, gradient chizig'ining %).
 */
export function curtainGeometry(width: number, height: number) {
  const angle = (CURTAIN_ANGLE * Math.PI) / 180;
  const sin = Math.sin(angle);
  const cos = Math.abs(Math.cos(angle));
  const fade = CURTAIN_FADE * width;
  const slant = (height * cos) / sin; // qiya chegaraning ekran balandligi bo'ylab gorizontal siljishi
  const layer = 2 * width + fade + slant;
  const lineLength = layer * sin + height * cos;
  const half = (fade * sin) / 2 / lineLength;
  return { size: (layer / width) * 100, from: (0.5 - half) * 100, to: (0.5 + half) * 100 };
}

type ViewTransition = { finished: Promise<void>; skipTransition: () => void };
type DocumentWithTransitions = Document & { startViewTransition?: (update: () => void) => ViewTransition };

// Joriy almashish: qayta bosilsa u darhol tugatiladi va yangisi boshlanadi (tugma hech qachon "kutdirmaydi")
let running: ViewTransition | null = null;
let switchId = 0;

/** Rejimni ekranda almashtiradi; imkon bo'lsa parda animatsiyasi bilan (saqlamaydi) */
function switchTo(next: Theme) {
  if (next === current) return;
  const doc = document as DocumentWithTransitions;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  if (!doc.startViewTransition || reduced) {
    apply(next);
    return;
  }
  const root = document.documentElement;
  const id = ++switchId;
  running?.skipTransition();
  const g = curtainGeometry(window.innerWidth, window.innerHeight);
  root.style.setProperty("--vt-size", `${g.size}%`);
  root.style.setProperty("--vt-from", `${g.from}%`);
  root.style.setProperty("--vt-to", `${g.to}%`);
  root.dataset.themeSwitch = next;
  // flushSync: tugmadagi belgi ham yangi ko'rinishga tushsin (React keyinroq chizmasin)
  const transition = doc.startViewTransition(() => flushSync(() => apply(next)));
  running = transition;
  transition.finished.finally(() => {
    // Eski almashishning yakuni yangisining belgisini o'chirib yubormasin
    if (id !== switchId) return;
    delete root.dataset.themeSwitch;
    running = null;
  });
}

/** Tanlovni saqlaydi (light / dark / system) va kerak bo'lsa rejimni almashtiradi */
export function setPreference(pref: ThemePreference) {
  try {
    if (pref === "system") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* ixtiyoriy: maxfiy rejim */
  }
  const next = pref === "system" ? resolveTheme(null, systemDark()) : pref;
  if (next === current) listeners.forEach((l) => l()); // faqat tanlov o'zgardi (masalan dark -> system)
  else switchTo(next);
}

/** Rejimni almashtiradi va saqlaydi (ikki holatli tugma uchun) */
export function setTheme(next: Theme) {
  setPreference(next);
}

/** Boshqa tab yoki tizim sozlamasi o'zgarsa -- animatsiyasiz ergashadi */
export function startThemeSync() {
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_KEY) apply(resolveTheme(e.newValue, systemDark()));
  };
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onSystem = () => {
    if (!readStored()) apply(resolveTheme(null, systemDark()));
  };
  window.addEventListener("storage", onStorage);
  media?.addEventListener?.("change", onSystem);
  return () => {
    window.removeEventListener("storage", onStorage);
    media?.removeEventListener?.("change", onSystem);
  };
}
